import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Download, RefreshCw } from 'lucide-react';
import { useAppState } from '@/core/app-context';
import { downloadFile } from '@/core/services/file-download';
import { EmptyTenantState } from '@/components/EmptyTenantState';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { fetchOverview, fetchBehavior, fetchEngagement, fetchFunnel } from '@/features/analytics/api';
import { OverviewTab } from '@/features/analytics/tabs/OverviewTab';
import { EngagementTab } from '@/features/analytics/tabs/EngagementTab';
import { FunnelTab } from '@/features/analytics/tabs/FunnelTab';
import { BehaviorTab } from '@/features/analytics/tabs/BehaviorTab';
import { QualityTab } from '@/features/analytics/tabs/QualityTab';
import { ErrorsTab } from '@/features/analytics/tabs/ErrorsTab';
import type { AnalyticsPeriodDays } from '@/features/analytics/types';

export const Route = createFileRoute('/analytics')({
  head: () => ({
    meta: [{ title: 'Analytics · Replya Internal Backoffice' }],
  }),
  component: AnalyticsPage,
});

const PERIODS: { value: AnalyticsPeriodDays; label: string }[] = [
  { value: 7, label: '7 giorni' },
  { value: 30, label: '30 giorni' },
  { value: 90, label: '90 giorni' },
];

const TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'engagement', label: 'Engagement' },
  { value: 'funnel', label: 'Funnel' },
  { value: 'behavior', label: 'Comportamento' },
  { value: 'quality', label: 'Qualità chatbot' },
  { value: 'errors', label: 'Errori & Crash' },
];

function toCsv(rows: (string | number)[][]): string {
  return rows
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');
}

/**
 * Esporta i KPI e le serie principali. Scarica i dati al volo invece di
 * dipendere dalla tab attiva, così l'export è completo da qualunque vista.
 */
async function exportCsv(tenantId: string, tenantName: string, days: number) {
  const [overview, engagement, funnel, behavior] = await Promise.all([
    fetchOverview(tenantId, days),
    fetchEngagement(tenantId, days),
    fetchFunnel(tenantId, days),
    fetchBehavior(tenantId, days),
  ]);

  const rows: (string | number)[][] = [
    ['KPI', 'Periodo corrente', 'Periodo precedente'],
    ...Object.entries(overview.kpi)
      .filter(([, value]) => typeof value === 'number')
      .map(([key, value]) => [
        key,
        value as number,
        (overview.previous[key as keyof typeof overview.previous] as number) ?? 0,
      ]),
    [],
    ['Data', 'Utenti attivi', 'Sessioni', 'Eventi', 'Conversazioni', 'Messaggi'],
    ...engagement.daily.map((d) => [d.date, d.activeUsers, d.sessions, d.events, d.conversations, d.messages]),
    [],
    ['Step funnel', 'Sessioni', '% dall\'ingresso', '% dal precedente'],
    ...funnel.steps.map((s) => [s.label, s.sessions, s.conversionFromEntry, s.conversionFromPrevious]),
    [],
    ['Pagina più vista', 'Visite'],
    ...behavior.topPages.map((p) => [p.key, p.count ?? 0]),
    [],
    ['Elemento cliccato', 'Click'],
    ...behavior.topClicks.map((c) => [c.key, c.count ?? 0]),
  ];

  downloadFile(toCsv(rows), `analytics-${tenantName}-${days}g.csv`, 'text/csv');
}

function AnalyticsPage() {
  const { selectedTenant } = useAppState();
  const queryClient = useQueryClient();
  const [days, setDays] = useState<AnalyticsPeriodDays>(30);
  const [tab, setTab] = useState('overview');
  const [exporting, setExporting] = useState(false);

  if (!selectedTenant) {
    return <EmptyTenantState />;
  }

  const tenantId = selectedTenant.tenantId;

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportCsv(tenantId, selectedTenant.name, days);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <PageHeader title="Analytics" subtitle={`Utilizzo, KPI e affidabilità per ${selectedTenant.name}`}>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => queryClient.invalidateQueries({ queryKey: ['analytics'] })}
            className="rounded-xl"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Aggiorna
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={exporting}
            className="rounded-xl"
          >
            <Download className={`mr-2 h-4 w-4 ${exporting ? 'animate-pulse' : ''}`} />
            Esporta CSV
          </Button>
        </div>
      </PageHeader>

      <div className="flex flex-wrap items-center gap-2">
        {PERIODS.map((p) => (
          <button
            key={p.value}
            type="button"
            onClick={() => setDays(p.value)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
              days === p.value
                ? 'bg-brand text-brand-foreground shadow-glow-sm'
                : 'bg-surface-2 text-muted-foreground hover:text-foreground hover:bg-surface-3'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-6">
        <TabsList className="flex w-full flex-wrap justify-start gap-1 rounded-xl bg-surface-2 p-1">
          {TABS.map((t) => (
            <TabsTrigger
              key={t.value}
              value={t.value}
              className="rounded-lg data-[state=active]:bg-brand data-[state=active]:text-brand-foreground"
            >
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Le tab montano la propria query solo quando sono attive: evita
            sei aggregazioni Mongo in parallelo all'apertura della pagina. */}
        <TabsContent value="overview">
          {tab === 'overview' && <OverviewTab tenantId={tenantId} days={days} />}
        </TabsContent>
        <TabsContent value="engagement">
          {tab === 'engagement' && <EngagementTab tenantId={tenantId} days={days} />}
        </TabsContent>
        <TabsContent value="funnel">
          {tab === 'funnel' && <FunnelTab tenantId={tenantId} days={days} />}
        </TabsContent>
        <TabsContent value="behavior">
          {tab === 'behavior' && <BehaviorTab tenantId={tenantId} days={days} />}
        </TabsContent>
        <TabsContent value="quality">
          {tab === 'quality' && <QualityTab tenantId={tenantId} days={days} />}
        </TabsContent>
        <TabsContent value="errors">
          {tab === 'errors' && <ErrorsTab tenantId={tenantId} days={days} />}
        </TabsContent>
      </Tabs>
    </div>
  );
}
